import { isChatStatusBusy, useStores } from '@littlebird/sdk';
import { reaction } from 'mobx';
import { useEffect, useState } from 'react';

import { chatRowLayoutStore } from '@/features/chat/chat-row-layout.store';

import { currentTurnUsesBrowser } from './browser-stream-activity';
import {
  BrowserViewerConnections,
  BrowserViewerStore,
} from './browser-viewer.store';

const connections = new BrowserViewerConnections();

export function useBrowserViewer(
  threadId: string,
  browserActive: boolean,
  isSelected = true
) {
  const { messages, threads } = useStores();
  const [viewer] = useState(() => new BrowserViewerStore(connections));

  useEffect(() => viewer.setSelected(isSelected), [isSelected, viewer]);

  useEffect(() => {
    let hasStartedViewing = false;
    const updateAvailability = () => {
      viewer.setAvailable(
        browserActive &&
          (!viewer.isSelected || !chatRowLayoutStore.isBrowserDismissed) &&
          navigator.onLine
      );
      // Open the initial view once; returning from a pause still needs Resume
      if (
        viewer.available &&
        viewer.isSelected &&
        document.visibilityState !== 'hidden' &&
        !hasStartedViewing
      ) {
        hasStartedViewing = true;
        viewer.resume();
      }
    };
    const stopLayout = reaction(
      () => [viewer.isSelected, chatRowLayoutStore.isBrowserDismissed],
      updateAvailability,
      { fireImmediately: true }
    );
    document.addEventListener('visibilitychange', updateAvailability);
    window.addEventListener('online', updateAvailability);
    window.addEventListener('offline', updateAvailability);
    window.addEventListener('pageshow', updateAvailability);
    const onPageHide = () => viewer.setAvailable(false);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      stopLayout();
      document.removeEventListener('visibilitychange', updateAvailability);
      window.removeEventListener('online', updateAvailability);
      window.removeEventListener('offline', updateAvailability);
      window.removeEventListener('pageshow', updateAvailability);
      window.removeEventListener('pagehide', onPageHide);
      viewer.setAvailable(false);
    };
  }, [browserActive, viewer]);

  useEffect(() => {
    return reaction(
      () =>
        isChatStatusBusy(threads.getById(threadId)?.status) &&
        currentTurnUsesBrowser(messages.getMessagesByThreadId(threadId) ?? []),
      viewer.setBrowserTurnActive,
      { fireImmediately: true }
    );
  }, [messages, threads, threadId, viewer]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    const stop = reaction(
      () => viewer.isViewing,
      (viewing) => {
        clearInterval(timer);
        if (viewing) timer = setInterval(viewer.tick, 1_000);
      },
      { fireImmediately: true }
    );
    return () => {
      stop();
      clearInterval(timer);
    };
  }, [viewer]);

  return viewer;
}
